import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '~/lib/api-client';
import { liveCallJobKeys } from '~/lib/query-keys';

/**
 * FRONT-2991: the EDDY jobs of the voice call running in a chat — what the
 * hands-free strip shows one row each. Polled while the call is up: jobs
 * change state on the server (a turn ends, a card opens) with nothing in the
 * browser to tell it.
 */

export type LiveCallJobState = 'queued' | 'working' | 'needs-you' | 'done' | 'failed' | 'cancelled';

export interface LiveCallJob {
  id: string;
  name: string;
  state: LiveCallJobState;
  /** The job ahead of this one, while it waits. */
  startsAfter: string | null;
  /** The job this one follows up on. */
  followUpOf: string | null;
  /** Set when the job runs in its own chat (the user asked for it in parallel). */
  chatSessionId: string | null;
  /** What the call said when the job ended. */
  result: string | null;
  createdAt: string;
}

const POLL_MS = 2_500;

export function useLiveCallJobs(sessionId: string | null, active: boolean) {
  return useQuery({
    queryKey: liveCallJobKeys.byChat(sessionId ?? ''),
    queryFn: async () => (await api.get<{ jobs: LiveCallJob[] }>(`/api/voice/live/jobs?sessionId=${encodeURIComponent(sessionId!)}`)).jobs,
    enabled: active && !!sessionId,
    // Only "this call has no jobs here" (GPT-Live off: 404/403) stops asking;
    // a passing failure (a redeploy, a network blip) keeps the poll going.
    refetchInterval: (q) => (active && !isFinalJobsError(q.state.error) ? POLL_MS : false),
    retry: (count, err) => !isFinalJobsError(err) && count < 2,
  });
}

export function isFinalJobsError(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false;
  return err.status === 404 || err.status === 403;
}

/** The strip's Stop: the same cancel the call does by voice. Shown stopped at once; the next poll confirms. */
export function useCancelLiveCallJob(sessionId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (jobId: string) =>
      api.post<{ ok: boolean; status: string; message: string }>(`/api/voice/live/jobs/${encodeURIComponent(jobId)}/cancel`, {}),
    onMutate: async (jobId) => {
      if (!sessionId) return;
      const key = liveCallJobKeys.byChat(sessionId);
      await queryClient.cancelQueries({ queryKey: key });
      queryClient.setQueryData<LiveCallJob[]>(key, (jobs) =>
        jobs?.map((j) => (j.id === jobId ? { ...j, state: 'cancelled' } : j)),
      );
    },
    onSettled: () => {
      if (sessionId) void queryClient.invalidateQueries({ queryKey: liveCallJobKeys.byChat(sessionId) });
    },
  });
}
